// Build identity for the reload-on-deploy poller. NEXT_PUBLIC_APP_VERSION is
// stamped from VERCEL_GIT_COMMIT_SHA at build time (next.config.mjs) and
// inlined into both the client bundle and the server runtime, so the client
// constant and /api/version diverge exactly when a newer deploy is live.
export const APP_VERSION: string = process.env.NEXT_PUBLIC_APP_VERSION ?? "dev"

export function shouldPromptReload(current: string, fetched: unknown): boolean {
  if (typeof fetched !== "string" || fetched.length === 0) return false
  // "dev" means no build stamp (local dev / preview without SHA) — a
  // comparison against it is meaningless and would toast every 5 minutes.
  if (current === "dev" || fetched === "dev") return false
  return fetched !== current
}

export type UpdateAction = "reload" | "prompt" | "none"

export interface UpdateDecisionInput {
  /** The version baked into the running bundle (APP_VERSION). */
  current: string
  /** The version reported by /api/version (untrusted shape). */
  fetched: unknown
  visibilityState: "visible" | "hidden"
  /**
   * Version an auto-reload already fired for in this browser session
   * (sessionStorage flag) — the loop guard. null/undefined = never reloaded.
   */
  alreadyReloadedFor?: string | null
}

/**
 * Update policy for VersionPoller, kept pure so it is unit-testable:
 * - newer deploy + document hidden → "reload" (auto-apply — nobody is
 *   mid-task on a hidden tablet; they sleep between meal services)
 * - newer deploy + visible → "prompt" (persistent "Reload now" toast;
 *   the human stays in control while working)
 * - no update, same version, dev build, or malformed response → "none"
 * - loop guard: never "reload" twice for the same fetched version — if we
 *   already auto-reloaded for it and still see it as different, reloading
 *   again would spin forever (rollback/CDN cache); fall back to "none".
 */
export function decideUpdateAction(input: UpdateDecisionInput): UpdateAction {
  if (!shouldPromptReload(input.current, input.fetched)) return "none"
  if (input.visibilityState === "hidden") {
    return input.alreadyReloadedFor === input.fetched ? "none" : "reload"
  }
  return "prompt"
}
